/**
 * spraak-wav.js — een microfoonopname omzetten naar het formaat waarin de transcriptie het best
 * werkt: 16 kHz, mono, 16-bit PCM (wav).
 *
 * Gemeten op 15-10-2026 (4 echte veldopnames, gpt-4o-transcribe met de NEN 5104-woordenlijst,
 * elk formaat twee keer): op 44,1 of 48 kHz ging één opname volledig mis (58% woordfouten,
 * "Boring B5 startte op…" in plaats van "Boring B55 van 0 tot 20"), op 12–24 kHz niet (25%,
 * de rest is verschil met de referentietekst). 16 kHz wav was het beste en het stabielst; 8 kHz
 * is te weinig ("ziltig"). De browser neemt op in webm/opus op 48 kHz, dus precies in het
 * slechte bereik -- daarom zetten we hier om vóór het uploaden.
 *
 * Grootte: 32 kB per seconde (3 minuten ≈ 5,8 MB). Voor een upload vanaf een laptop prima.
 */

export const SPRAAK_SAMPLERATE = 16000;

/** Float32-samples (-1..1, mono) → een wav-bestand (16-bit PCM). Los te testen, zonder browser. */
export function pcmNaarWav(samples, sampleRate = SPRAAK_SAMPLERATE) {
  const n = samples.length;
  const buf = new ArrayBuffer(44 + n * 2);
  const v = new DataView(buf);
  const tekst = (o, s) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  tekst(0, 'RIFF'); v.setUint32(4, 36 + n * 2, true); tekst(8, 'WAVE');
  tekst(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, sampleRate, true); v.setUint32(28, sampleRate * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  tekst(36, 'data'); v.setUint32(40, n * 2, true);
  for (let i = 0; i < n; i++) {
    const x = Math.max(-1, Math.min(1, samples[i]));
    v.setInt16(44 + i * 2, x < 0 ? x * 0x8000 : x * 0x7fff, true);
  }
  return new Uint8Array(buf);
}

/**
 * Een opgenomen blob (webm/opus, mp4, …) → een 16 kHz mono wav-blob. De browser decodeert en
 * resamplet zelf (OfflineAudioContext), met een goed filter. Gooit bij een fout; de aanroeper
 * valt dan terug op het origineel -- liever een opname in het oude formaat dan geen opname.
 */
export async function naarSpraakWav(blob, sampleRate = SPRAAK_SAMPLERATE) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  const Offline = window.OfflineAudioContext || window.webkitOfflineAudioContext;
  if (!Ctx || !Offline) throw new Error('Web Audio niet beschikbaar');
  const ctx = new Ctx();
  let bron;
  try {
    bron = await ctx.decodeAudioData(await blob.arrayBuffer());
  } finally {
    ctx.close?.();
  }
  const lengte = Math.ceil(bron.duration * sampleRate);
  if (!lengte) throw new Error('lege opname');
  // Eén uitgangskanaal: Web Audio mengt meerdere kanalen dan netjes naar mono.
  const offline = new Offline(1, lengte, sampleRate);
  const node = offline.createBufferSource();
  node.buffer = bron;
  node.connect(offline.destination);
  node.start();
  const uit = await offline.startRendering();
  return new Blob([pcmNaarWav(uit.getChannelData(0), sampleRate)], { type: 'audio/wav' });
}
